import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import Pagination from '../components/Pagination';
import { MultiSelect } from '../components/MultiSelect';
import './Autopilot.css';
import {
  autopilotApi,
  type ApprovalRecord,
  type AutoRuleExecutionJob,
  type AutoRuleExecutionSummary,
  type AutoRuleRun,
  type AutopilotActionType,
  type AutopilotChannel,
  type OperationLog,
  type SuggestionDetail,
} from '@/api/autopilot';
import { useAppStore } from '@/store/useAppStore';
import { useToastStore } from '@/store/useToastStore';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';

type Toast = { type: 'success' | 'error'; message: string } | null;
type AutopilotTab = 'overview' | 'stream' | 'logs';

const REJECT_PRESETS = ['数据样本不足', '当前策略不适用', '需要人工继续观察', '暂不执行该建议'];
const AUTOPILOT_DISPLAY_TIMEZONE = 'Asia/Shanghai';

function parseAutopilotTime(value: string): Date {
  const raw = (value || '').trim();
  if (!raw) return new Date(NaN);
  const normalized = raw.replace(' ', 'T');
  if (/[zZ]$|[+-]\d{2}:\d{2}$/.test(normalized)) {
    return new Date(normalized);
  }
  return new Date(`${normalized}Z`);
}

function getDateTimeParts(date: Date, timeZone?: string): Record<string, string> | null {
  if (Number.isNaN(date.getTime())) return null;
  try {
    const formatter = new Intl.DateTimeFormat('zh-CN', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
  } catch {
    return Object.fromEntries(
      new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(date).map((part) => [part.type, part.value])
    );
  }
}

function formatActionSummary(detail: SuggestionDetail): string {
  const action = detail.action || {};
  const actions: string[] = [];
  if (action.pause) actions.push('暂停投放');
  if (typeof action.budget_pct === 'number' && action.budget_pct !== 0) {
    actions.push(`预算调整 ${action.budget_pct > 0 ? '+' : ''}${action.budget_pct}%`);
  }
  if (typeof action.new_budget === 'number' && action.new_budget > 0) {
    actions.push(`预算改为 ${action.new_budget}`);
  }
  if (typeof action.bid_pct === 'number' && action.bid_pct !== 0) {
    actions.push(`出价调整 ${action.bid_pct > 0 ? '+' : ''}${action.bid_pct}%`);
  }
  if (typeof action.new_bid === 'number' && action.new_bid > 0) {
    actions.push(`出价改为 ${action.new_bid}`);
  }
  return actions.length > 0 ? actions.join(' / ') : '保持原策略';
}

function formatShortTime(value: string, timeZone?: string): string {
  const date = parseAutopilotTime(value);
  const parts = getDateTimeParts(date, timeZone);
  if (!parts) return '-';
  return `${parts.month}/${parts.day} ${parts.hour}:${parts.minute}`;
}

function formatRelativeDayTime(value: string, timeZone?: string): string {
  const date = parseAutopilotTime(value);
  const dateParts = getDateTimeParts(date, timeZone);
  if (!dateParts) return '-';
  const now = new Date();
  const nowParts = getDateTimeParts(now, timeZone);
  if (!nowParts) return '-';
  const startNow = Date.UTC(Number(nowParts.year), Number(nowParts.month) - 1, Number(nowParts.day));
  const startDate = Date.UTC(Number(dateParts.year), Number(dateParts.month) - 1, Number(dateParts.day));
  const diffDays = Math.max(0, Math.floor((startNow - startDate) / 86400000));
  const hhmm = `${dateParts.hour}:${dateParts.minute}`;
  if (diffDays === 0) return `今天 ${hhmm}`;
  if (diffDays === 1) return `昨天 ${hhmm}`;
  if (diffDays === 2) return `前天 ${hhmm}`;
  return `${diffDays}天前 ${hhmm}`;
}

function executionLabel(summary: AutoRuleExecutionSummary | null, job: AutoRuleExecutionJob | null): string {
  if (job?.status === 'queued') return '等待执行';
  if (job?.status === 'running') return '执行中';
  if (job?.status === 'failed') return '执行失败';
  if (!summary) return '尚未执行';
  return `处理 ${summary.processed_rules} / 命中 ${summary.matched} / 创建 ${summary.created}`;
}

function waitForExecutionPoll(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function decisionLabel(decision: string): { text: string; cls: string } {
  switch (decision) {
    case 'pending_approval':
      return { text: '待审批', cls: 'pending' };
    case 'dedup_skipped':
      return { text: '去重跳过', cls: 'muted' };
    case 'not_matched':
      return { text: '未命中', cls: 'muted' };
    case 'unsupported_action':
      return { text: '动作不支持', cls: 'danger' };
    case 'reference_unavailable':
      return { text: '缺少目标值', cls: 'muted' };
    case 'dedup_exclusive_group':
      return { text: '优先级跳过', cls: 'muted' };
    default:
      return { text: decision || '-', cls: 'muted' };
  }
}

function actionTypeLabel(actionType: string | undefined): string {
  switch (actionType) {
    case 'circuit':
      return '暂停投放';
    case 'scale':
      return '预算扩量';
    case 'bid':
      return '调整出价';
    case 'notice':
      return '通知提醒';
    case 'execute':
      return '执行任务';
    default:
      return actionType || '-';
  }
}

function channelLabel(channel?: string): string {
  switch (channel) {
    case 'fb':
      return 'Facebook';
    case 'tt':
      return 'TikTok';
    case 'gg':
      return 'Google';
    default:
      return channel || '-';
  }
}

/** 与后端 normalizeChannel 对齐，用于筛选与标签样式（接口可能返回 facebook / fb 等） */
function normalizeChannelKey(platform?: string): AutopilotChannel | null {
  const raw = (platform || '').toLowerCase().trim();
  if (['facebook', 'fb', 'meta'].includes(raw)) return 'fb';
  if (['tiktok', 'tt'].includes(raw)) return 'tt';
  if (['google', 'gg', 'google_ads'].includes(raw)) return 'gg';
  return null;
}

function channelLabelFromPlatform(platform?: string): string {
  const k = normalizeChannelKey(platform);
  if (k) return channelLabel(k);
  return (platform || '').trim() || '-';
}

function platformTagClassFromPlatform(platform?: string): string {
  const k = normalizeChannelKey(platform);
  if (k === 'tt') return 'tt';
  if (k === 'gg') return 'gg';
  return 'fb';
}

function actionTagClass(actionType?: string): string {
	if (actionType === 'notice') return 'notice';
  if (actionType === 'bid') return 'bid';
  if (actionType === 'scale') return 'scale';
  return 'circuit';
}

function isSuggestionDetail(value: unknown): value is SuggestionDetail {
  return (
    !!value &&
    typeof value === 'object' &&
    'title' in (value as Record<string, unknown>) &&
    'reason' in (value as Record<string, unknown>)
  );
}

function operationPayload(log: OperationLog): SuggestionDetail | null {
  const candidates = [log.modified_value, log.original_value];
  for (const candidate of candidates) {
    if (isSuggestionDetail(candidate)) return candidate;
  }
  return null;
}

function triggerSnapshots(detail?: SuggestionDetail | null): Array<Record<string, unknown>> {
  return Array.isArray(detail?.trigger_snapshot) ? detail.trigger_snapshot : [];
}

function triggerLine(item: Record<string, unknown>): string {
  const metric = String(item.metric || '-');
  const compareType = String(item.compare_type || 'absolute');
  const operator = String(item.operator || '-');
  const current = item.current_value == null ? '-' : Number(item.current_value).toFixed(2);
  const baselineRaw = item.baseline ?? item.baseline_value;
  const baseline = baselineRaw == null ? '' : `，基线 ${Number(baselineRaw).toFixed(2)}`;
  const ratioRaw = item.ratio;
  const ratio = ratioRaw == null ? '' : `，变化 ${Number(ratioRaw).toFixed(2)}`;
  const threshold = item.threshold == null ? '-' : String(item.threshold);
  if (compareType === 'percent') {
    return `${metric} 需 ${operator} ${threshold}，当前 ${current}${baseline}${ratio}`;
  }
  return `${metric} ${operator} ${threshold}，当前 ${current}${baseline}`;
}

function targetDisplay(run: AutoRuleRun): string {
  const name = (run.target_name || '').trim();
  if (run.target_type === 'account') {
    if (name && run.platform) return `${name} / ${run.platform}`;
    return name || run.platform || run.target_id;
  }
  return name || run.target_id;
}

function queueTargetDisplay(detail?: SuggestionDetail | null): string {
  const name = (detail?.target_name || '').trim();
  if (!name) return '-';
  const channel = channelLabel(detail?.channel);
  return channel !== '-' ? `${name} / ${channel}` : name;
}

function executionResultLabel(result: OperationLog['execution_result']): { text: string; cls: string } {
  switch (result) {
    case 'success':
      return { text: '执行成功', cls: 'success' };
    case 'partial':
      return { text: '部分执行', cls: 'warning' };
    case 'failed':
      return { text: '执行失败', cls: 'danger' };
    case 'rejected':
      return { text: '已驳回', cls: 'muted' };
    default:
      return { text: result || '-', cls: 'muted' };
  }
}

function runReason(run: AutoRuleRun): string {
  const snapshot = run.trigger_snapshot || [];
  if (!Array.isArray(snapshot) || snapshot.length === 0) return '-';
  return snapshot
    .map((item) => {
      const metric = String(item.metric || '-');
      const current = item.current_value == null ? '-' : Number(item.current_value).toFixed(2);
      const threshold = item.threshold == null ? '-' : String(item.threshold);
      return `${metric}: ${current} vs ${threshold}`;
    })
    .join(' / ');
}

function runActionType(run: AutoRuleRun): AutopilotActionType | null {
  const snapshot = (run.action_snapshot || {}) as Record<string, unknown>;
  const explicitType = typeof snapshot.action_type === 'string' ? snapshot.action_type : '';
  if (explicitType === 'circuit' || explicitType === 'scale' || explicitType === 'bid') {
    return explicitType;
  }
  if (typeof snapshot.bid_pct === 'number' || typeof snapshot.new_bid === 'number') return 'bid';
  if (typeof snapshot.budget_pct === 'number' || typeof snapshot.new_budget === 'number') return 'scale';
  if (snapshot.pause === true) return 'circuit';
  const legacyRuleSnapshot =
    snapshot.rule_snapshot && typeof snapshot.rule_snapshot === 'object'
      ? (snapshot.rule_snapshot as Record<string, unknown>)
      : null;
  const legacyActions = Array.isArray(snapshot.actions)
    ? snapshot.actions
    : Array.isArray(legacyRuleSnapshot?.actions)
      ? (legacyRuleSnapshot.actions as unknown[])
      : [];
  const legacyActionType =
    legacyActions.length > 0 && legacyActions[0] && typeof legacyActions[0] === 'object'
      ? String((legacyActions[0] as Record<string, unknown>).type || '').toLowerCase()
      : '';
  if (legacyActionType === 'close') return 'circuit';
  if (legacyActionType === 'increase_budget' || legacyActionType === 'decrease_budget') return 'scale';
  if (legacyActionType === 'adjust_bid' || legacyActionType === 'increase_bid' || legacyActionType === 'decrease_bid') {
    return 'bid';
  }
  return null;
}

export default function Autopilot() {
  const { brandId, siteId, hasPermission, adContext, permissionsLoaded, sitesLoaded } = useAppStore();
  const showToast = useToastStore((s) => s.show);
  const userLevel = adContext?.data_level ?? 1;

  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  const [lastExecution, setLastExecution] = useState<AutoRuleExecutionSummary | null>(null);
  const [executionJob, setExecutionJob] = useState<AutoRuleExecutionJob | null>(null);

  const [queue, setQueue] = useState<ApprovalRecord[]>([]);
  const [queuePage, setQueuePage] = useState(1);
  const [queueSize, setQueueSize] = useState(10);
  const [queueTotal, setQueueTotal] = useState(0);
  const [queueRuleFilter, setQueueRuleFilter] = useState('all');
  const [bulkActing, setBulkActing] = useState<'approve' | 'reject' | null>(null);

  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);
  const [logSize, setLogSize] = useState(20);

  const [ruleRuns, setRuleRuns] = useState<AutoRuleRun[]>([]);
  const [ruleRunPage, setRuleRunPage] = useState(1);
  const [ruleRunSize, setRuleRunSize] = useState(10);
  const [ruleRunTotal, setRuleRunTotal] = useState(0);
  const [ruleRunOverallTotal, setRuleRunOverallTotal] = useState(0);
  const [streamLoading, setStreamLoading] = useState(false);

  const [activeTab, setActiveTab] = useState<AutopilotTab>('overview');
  const [streamPlatformFilter, setStreamPlatformFilter] = useState<'all' | AutopilotChannel>('all');
  const [streamActionFilter, setStreamActionFilter] = useState<'all' | AutopilotActionType>('all');
  const [logFilter, setLogFilter] = useState<'all' | OperationLog['execution_result']>('all');
  const [expandedRunID, setExpandedRunID] = useState<string | null>(null);

  const [confirmItem, setConfirmItem] = useState<ApprovalRecord | null>(null);
  const [approvalNote, setApprovalNote] = useState('');
  const [rejectItem, setRejectItem] = useState<ApprovalRecord | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const streamLoadedKeyRef = useRef('');
  const streamInFlightKeyRef = useRef('');
  const activeRequestKeyRef = useRef('');
  const executionPollTokenRef = useRef(0);

  const pendingQueue = useMemo(() => queue.filter((item) => item.decision === 'pending'), [queue]);
  const queueRuleOptions = useMemo(() => {
    const names = Array.from(
      new Set(
        pendingQueue
          .map((item) => String(item.suggestion.rule_name || '').trim())
          .filter(Boolean)
      )
    );
    return names.sort((a, b) => a.localeCompare(b, 'zh-CN'));
  }, [pendingQueue]);
  const visiblePendingQueue = useMemo(() => {
    if (queueRuleFilter === 'all') return pendingQueue;
    return pendingQueue.filter((item) => (item.suggestion.rule_name || '').trim() === queueRuleFilter);
  }, [pendingQueue, queueRuleFilter]);
  const queuePageCount = Math.max(1, Math.ceil(queueTotal / queueSize));

  const ruleRunPageCount = Math.max(1, Math.ceil(ruleRunTotal / ruleRunSize));
  const streamDisplayTotal = ruleRunTotal;
  const autopilotScopeReady = permissionsLoaded && sitesLoaded && !!brandId && !!siteId;
  const autopilotScopeKey = `${brandId}|${siteId}`;
  activeRequestKeyRef.current = `${autopilotScopeKey}|${activeTab}|${queuePage}|${queueSize}|${ruleRunPage}|${ruleRunSize}|${streamPlatformFilter}|${streamActionFilter}|${logPage}|${logSize}|${logFilter}`;
  const streamRequestKey = `${brandId}|${siteId}|${ruleRunPage}|${ruleRunSize}|${streamPlatformFilter}|${streamActionFilter}`;

  const clientLogFilter = logFilter !== 'all';
  const filteredLogs = useMemo(
    () => (clientLogFilter ? logs.filter((log) => log.execution_result === logFilter) : logs),
    [clientLogFilter, logFilter, logs]
  );
  const logListTotal = clientLogFilter ? filteredLogs.length : logTotal;
  const logPageCount = Math.max(1, Math.ceil(logListTotal / logSize));
  const pagedLogs = useMemo(() => {
    if (!clientLogFilter) return logs;
    return filteredLogs.slice((logPage - 1) * logSize, logPage * logSize);
  }, [clientLogFilter, filteredLogs, logPage, logSize, logs]);

  const loadOverviewData = async () => {
    if (!autopilotScopeReady) return;
    const requestKey = activeRequestKeyRef.current;
    setLoading(true);
    try {
      const [queueRes, logsRes, runsRes] = await Promise.all([
        autopilotApi.getQueue({ brand_id: brandId, decision: 'pending', site_id: siteId, page: queuePage, size: queueSize }),
        autopilotApi.getLogs({ brand_id: brandId, page: 1, size: 1 }),
        autopilotApi.getRuleRuns({ brand_id: brandId, site_id: siteId, page: 1, size: 1 }),
      ]);
      if (activeRequestKeyRef.current !== requestKey) return;
      setQueue(queueRes.data.list || []);
      setQueueTotal(queueRes.data.total || 0);
      setLogTotal(logsRes.data.total || 0);
      setRuleRunOverallTotal(runsRes.data.total || 0);
    } catch (err) {
      setToast({ type: 'error', message: err instanceof Error ? err.message : '加载智能投手数据失败' });
    } finally {
      setLoading(false);
    }
  };

  const loadRuleRunsData = async () => {
    if (!autopilotScopeReady) return;
    if (streamInFlightKeyRef.current === streamRequestKey) return;
    streamInFlightKeyRef.current = streamRequestKey;
    const requestKey = activeRequestKeyRef.current;
    setStreamLoading(true);
    try {
      const runsRes = await autopilotApi.getRuleRuns({
        brand_id: brandId,
        site_id: siteId,
        channel: streamPlatformFilter === 'all' ? undefined : streamPlatformFilter,
        action_type: streamActionFilter === 'all' ? undefined : streamActionFilter,
        page: ruleRunPage,
        size: ruleRunSize,
      });
      if (activeRequestKeyRef.current !== requestKey) return;
      const list = runsRes.data.list || [];
      setRuleRuns(list);
      setRuleRunTotal(runsRes.data.total || 0);
      streamLoadedKeyRef.current = streamRequestKey;
    } catch (err) {
      setToast({ type: 'error', message: err instanceof Error ? err.message : '加载规则触发记录失败' });
    } finally {
      if (streamInFlightKeyRef.current === streamRequestKey) {
        streamInFlightKeyRef.current = '';
      }
      setStreamLoading(false);
    }
  };

  const loadLogsData = async () => {
    if (!autopilotScopeReady) return;
    const requestKey = activeRequestKeyRef.current;
    setLoading(true);
    try {
      const logClient = logFilter !== 'all';
      const logsRes = await autopilotApi.getLogs(logClient ? { brand_id: brandId, page: 1, size: 100 } : { brand_id: brandId, page: logPage, size: logSize });
      if (activeRequestKeyRef.current !== requestKey) return;
      setLogs(logsRes.data.list || []);
      setLogTotal(logsRes.data.total || 0);
    } catch (err) {
      setToast({ type: 'error', message: err instanceof Error ? err.message : '加载操作历史失败' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setQueuePage(1);
    setRuleRunPage(1);
    setLogPage(1);
    setExpandedRunID(null);
    setQueueRuleFilter('all');
    streamLoadedKeyRef.current = '';
    streamInFlightKeyRef.current = '';
  }, [brandId, siteId]);

  useEffect(() => {
    if (!autopilotScopeReady) return;
    if (activeTab !== 'overview') return;
    void loadOverviewData();
  }, [activeTab, autopilotScopeReady, brandId, siteId, queuePage, queueSize]);

  useEffect(() => {
    if (!autopilotScopeReady) return;
    if (activeTab !== 'stream') return;
    if (streamLoadedKeyRef.current === streamRequestKey) return;
    void loadRuleRunsData();
  }, [activeTab, autopilotScopeReady, streamRequestKey]);

  useEffect(() => {
    if (!autopilotScopeReady) return;
    if (activeTab !== 'logs') return;
    void loadLogsData();
  }, [activeTab, autopilotScopeReady, brandId, siteId, logPage, logSize, logFilter]);

  useEffect(() => {
    setRuleRunPage(1);
  }, [streamPlatformFilter, streamActionFilter]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const monitorExecutionJob = async (initialJob: AutoRuleExecutionJob, notifyOnFinish: boolean) => {
    const token = ++executionPollTokenRef.current;
    const scopeKey = `${brandId}|${siteId}`;
    let job = initialJob;
    setExecutionJob(job);
    setRunning(job.status === 'queued' || job.status === 'running');

    try {
      while (job.status === 'queued' || job.status === 'running') {
        await waitForExecutionPoll(3000);
        if (executionPollTokenRef.current !== token || `${brandId}|${siteId}` !== scopeKey) return;
        const res = await autopilotApi.getExecution(job.id, brandId, siteId);
        job = res.data;
        setExecutionJob(job);
      }
    } catch (err) {
      if (executionPollTokenRef.current !== token) return;
      setRunning(false);
      setToast({ type: 'error', message: err instanceof Error ? err.message : '查询自动规则执行状态失败' });
      return;
    }

    if (executionPollTokenRef.current !== token) return;
    setRunning(false);
    if (job.status === 'succeeded') {
      setLastExecution(job.summary || null);
      if (notifyOnFinish) {
        setToast({ type: 'success', message: '自动规则执行完成' });
      }
      await loadOverviewData();
      if (activeTab === 'stream') await loadRuleRunsData();
      return;
    }
    if (job.status === 'failed') {
      setToast({ type: 'error', message: job.error_message || '自动规则执行失败' });
    }
  };

  const executeNow = async () => {
    if (userLevel < 2) {
      showToast('你的角色为只读，无法执行规则');
      return;
    }
    if (!hasPermission('ad-mgmt:autopilot:config')) {
      showToast('暂无执行自动投手权限');
      return;
    }
    setRunning(true);
    try {
      const res = await autopilotApi.execute(brandId, siteId);
      setExecutionJob(res.data);
      setToast({
        type: 'success',
        message: res.data.status === 'running' ? '已有执行任务正在运行' : '执行任务已提交',
      });
      void monitorExecutionJob(res.data, true);
    } catch (err) {
      setRunning(false);
      setToast({ type: 'error', message: err instanceof Error ? err.message : '执行自动规则失败' });
    }
  };

  useEffect(() => {
    executionPollTokenRef.current += 1;
    setExecutionJob(null);
    setLastExecution(null);
    setRunning(false);
    if (!autopilotScopeReady) return;

    let cancelled = false;
    void (async () => {
      try {
        const res = await autopilotApi.getLatestExecution(brandId, siteId);
        if (cancelled || !res.data) return;
        setExecutionJob(res.data);
        if (res.data.status === 'succeeded') {
          setLastExecution(res.data.summary || null);
          return;
        }
        if (res.data.status === 'queued' || res.data.status === 'running') {
          void monitorExecutionJob(res.data, true);
        }
      } catch (err) {
        if (!cancelled) {
          setToast({ type: 'error', message: err instanceof Error ? err.message : '加载执行任务状态失败' });
        }
      }
    })();

    return () => {
      cancelled = true;
      executionPollTokenRef.current += 1;
    };
  }, [autopilotScopeReady, brandId, siteId]);

  const approveItem = async (item: ApprovalRecord) => {
    if (userLevel < 2) {
      showToast('你的角色为只读，无法执行审批');
      return;
    }
    if (!hasPermission('ad-mgmt:autopilot:approve')) {
      showToast('暂无审批权限');
      return;
    }
    try {
      const modifiedValue = item.action_type === 'notice' ? undefined : (item.modified_value || item.suggestion);
      await autopilotApi.approve(item.id, modifiedValue, approvalNote.trim() || undefined);
      setToast({
        type: 'success',
        message: item.action_type === 'notice' ? '已确认知悉' : '建议已批准并触发执行',
      });
      setConfirmItem(null);
      setApprovalNote('');
      await loadOverviewData();
      if (activeTab === 'stream') await loadRuleRunsData();
    } catch (err) {
      setToast({ type: 'error', message: err instanceof Error ? err.message : '批准失败' });
    }
  };

  const rejectCurrentItem = async (item: ApprovalRecord) => {
    if (userLevel < 2) {
      showToast('你的角色为只读，无法执行审批');
      return;
    }
    if (!hasPermission('ad-mgmt:autopilot:approve')) {
      showToast('暂无审批权限');
      return;
    }
    try {
      const note = rejectReason.trim() || undefined;
      await autopilotApi.reject(item.id, note, note);
      setToast({ type: 'success', message: '建议已驳回' });
      setRejectItem(null);
      setRejectReason('');
      await loadOverviewData();
      if (activeTab === 'stream') await loadRuleRunsData();
    } catch (err) {
      setToast({ type: 'error', message: err instanceof Error ? err.message : '驳回失败' });
    }
  };

  const bulkApproveVisibleQueue = async () => {
    if (visiblePendingQueue.length === 0) return;
    if (userLevel < 2) {
      showToast('你的角色为只读，无法执行审批');
      return;
    }
    if (!hasPermission('ad-mgmt:autopilot:approve')) {
      showToast('暂无审批权限');
      return;
    }
    if (!window.confirm(`确认全部批准当前筛选下的 ${visiblePendingQueue.length} 条建议吗？`)) return;

    setBulkActing('approve');
    try {
      for (const item of visiblePendingQueue) {
        const modifiedValue = item.action_type === 'notice' ? undefined : (item.modified_value || item.suggestion);
        await autopilotApi.approve(item.id, modifiedValue);
      }
      setToast({ type: 'success', message: `已全部批准 ${visiblePendingQueue.length} 条建议` });
      await loadOverviewData();
      if (activeTab === 'stream') await loadRuleRunsData();
    } catch (err) {
      setToast({ type: 'error', message: err instanceof Error ? err.message : '批量批准失败' });
    } finally {
      setBulkActing(null);
    }
  };

  const bulkRejectVisibleQueue = async () => {
    if (visiblePendingQueue.length === 0) return;
    if (userLevel < 2) {
      showToast('你的角色为只读，无法执行审批');
      return;
    }
    if (!hasPermission('ad-mgmt:autopilot:approve')) {
      showToast('暂无审批权限');
      return;
    }
    if (!window.confirm(`确认全部驳回当前筛选下的 ${visiblePendingQueue.length} 条建议吗？`)) return;

    setBulkActing('reject');
    try {
      for (const item of visiblePendingQueue) {
        await autopilotApi.reject(item.id, '批量驳回', '批量驳回');
      }
      setToast({ type: 'success', message: `已全部驳回 ${visiblePendingQueue.length} 条建议` });
      await loadOverviewData();
      if (activeTab === 'stream') await loadRuleRunsData();
    } catch (err) {
      setToast({ type: 'error', message: err instanceof Error ? err.message : '批量驳回失败' });
    } finally {
      setBulkActing(null);
    }
  };

  return (
    <div className="page-section autopilot-v2" style={{ display: 'block' }}>
      {toast && <div className={`autopilot-toast ${toast.type}`}>{toast.message}</div>}

      <div className="autopilot-tabs-shell">
        <nav className="autopilot-tabs-nav">
          <button
            type="button"
            className={`autopilot-tab-btn${activeTab === 'overview' ? ' active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            总览
            <span className="autopilot-tab-badge danger">{queueTotal}</span>
          </button>
          <button
            type="button"
            className={`autopilot-tab-btn${activeTab === 'stream' ? ' active' : ''}`}
            onClick={() => setActiveTab('stream')}
          >
            规则触发记录
            <span className="autopilot-tab-badge">{ruleRunOverallTotal}</span>
          </button>
          <button
            type="button"
            className={`autopilot-tab-btn${activeTab === 'logs' ? ' active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            操作历史
          </button>
        </nav>

        {activeTab === 'overview' && (
          <div className="autopilot-tab-panel active">
            <div className="autopilot-quick-bar">
              <div className="autopilot-quick-info">
                <div className="autopilot-quick-icon">⚡</div>
                <div>
                  <h4>自动投手执行中心</h4>
                  <p>规则来自经验库，系统会按计划自动检查并生成建议</p>
                </div>
              </div>
              <button className="btn btn-primary btn-sm" disabled={running || loading} onClick={() => void executeNow()}>
                {running ? (executionJob?.status === 'queued' ? '排队中...' : '执行中...') : '立即执行'}
              </button>
            </div>

            <div className="autopilot-stats-row">
              <button type="button" className="autopilot-stat-card">
                <div className="autopilot-stat-label">最近执行</div>
                <div className="autopilot-stat-value muted">{executionLabel(lastExecution, executionJob)}</div>
              </button>
              <button type="button" className="autopilot-stat-card highlight">
                <div className="autopilot-stat-label">待我处理</div>
                <div className="autopilot-stat-value primary">{queueTotal}</div>
              </button>
              <button type="button" className="autopilot-stat-card" onClick={() => setActiveTab('stream')}>
                <div className="autopilot-stat-label">规则触发记录</div>
                <div className="autopilot-stat-value">{ruleRunOverallTotal}</div>
              </button>
              <button type="button" className="autopilot-stat-card" onClick={() => setActiveTab('logs')}>
                <div className="autopilot-stat-label">操作历史</div>
                <div className="autopilot-stat-value">{logTotal}</div>
              </button>
            </div>

            <div className="autopilot-section-head">
              <h3>
                待我处理
                <span className="autopilot-tab-badge danger">{queueTotal}</span>
              </h3>
              <div className="autopilot-section-head-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={visiblePendingQueue.length === 0 || bulkActing !== null}
                  onClick={() => void bulkApproveVisibleQueue()}
                >
                  {bulkActing === 'approve' ? '全部批准中...' : '全部批准'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={visiblePendingQueue.length === 0 || bulkActing !== null}
                  onClick={() => void bulkRejectVisibleQueue()}
                >
                  {bulkActing === 'reject' ? '全部驳回中...' : '全部驳回'}
                </button>
                <MultiSelect
                  single
                  searchable
                  className="autopilot-rule-select"
                  options={[
                    { value: 'all', label: '筛选规则' },
                    ...queueRuleOptions.map((ruleName) => ({ value: ruleName, label: ruleName })),
                  ]}
                  value={[queueRuleFilter]}
                  onChange={(values) => setQueueRuleFilter(values[0] ?? 'all')}
                  placeholder="筛选规则"
                />
              </div>
            </div>

            {visiblePendingQueue.length === 0 ? (
              <div className="autopilot-empty">暂无待审批操作</div>
            ) : (
              visiblePendingQueue.map((item) => (
                <div className="autopilot-approval-card" key={item.id}>
                  <div className="autopilot-approval-meta">
                    <span className={`autopilot-urgency ${item.suggestion.urgency === 'high' ? 'high' : 'medium'}`}>
                      {item.suggestion.urgency === 'high' ? '紧急' : '关注'}
                    </span>
                    <span className="autopilot-trigger-time">
                      {formatShortTime(item.created_at, AUTOPILOT_DISPLAY_TIMEZONE)} 触发
                    </span>
                  </div>
                  <div className="autopilot-approval-title">{item.suggestion.title}</div>
                  <div className="autopilot-approval-details">
                    <div>
                      规则：<strong>{item.suggestion.rule_name || '-'}</strong>
                    </div>
                    <div>
                      对象：<strong>{queueTargetDisplay(item.suggestion)}</strong>
                    </div>
                    <div>
                      账户所属人：<strong>{item.suggestion.account_owner_name || '-'}</strong>
                    </div>
                    {item.suggestion.knowledge_description && (
                      <div>
                        经验动作：<strong>{item.suggestion.knowledge_description}</strong>
                      </div>
                    )}
                    <div>
                      原因：<strong>{item.suggestion.reason}</strong>
                    </div>
                  </div>

                  {triggerSnapshots(item.suggestion).length > 0 && (
                    <div className="autopilot-data-box">
                      {triggerSnapshots(item.suggestion).map((snapshot, index) => (
                        <div className="autopilot-kv" key={`${item.id}-${index}`}>
                          <span className="k">{String(snapshot.metric || '-')}</span>
                          <span className="v">{triggerLine(snapshot)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="autopilot-impact-row">
                    {(item.suggestion.impact || []).map((impact, index) => (
                      <div className="autopilot-impact-item" key={index}>
                        <span>{impact.label}</span>
                        <strong style={{ color: impact.color }}>{impact.value}</strong>
                      </div>
                    ))}
                  </div>

                  <div className="autopilot-approval-actions">
                    <button className="btn btn-outline btn-sm" onClick={() => setRejectItem(item)}>
                      驳回
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => setConfirmItem(item)}>
                      {item.action_type === 'notice' ? '确认已知悉' : '批准执行'}
                    </button>
                  </div>
                </div>
              ))
            )}

            {queueTotal > 0 && (
              <Pagination
                page={queuePage}
                totalPages={queuePageCount}
                onChange={(p) => setQueuePage(p)}
                total={`共 ${queueTotal} 条`}
                pageSize={queueSize}
                pageSizeOptions={[10, 20, 50, 100]}
                onPageSizeChange={(nextPageSize) => { setQueueSize(nextPageSize); setQueuePage(1); }}
                disabled={loading}
              />
            )}
          </div>
        )}

        {activeTab === 'stream' && (
          <div className="autopilot-tab-panel active">
            <div className="autopilot-filter-bar">
              {[
                { key: 'all', label: '全部' },
                { key: 'fb', label: 'Facebook' },
                { key: 'tt', label: 'TikTok' },
                { key: 'gg', label: 'Google' },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`autopilot-filter-chip${streamPlatformFilter === item.key ? ' active' : ''}`}
                  onClick={() => {
                    setRuleRunPage(1);
                    setStreamPlatformFilter(item.key as 'all' | AutopilotChannel);
                  }}
                >
                  {item.label}
                </button>
              ))}
              {[
                { key: 'circuit', label: '暂停投放' },
                { key: 'bid', label: '调整出价' },
                { key: 'scale', label: '预算扩量' },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`autopilot-filter-chip${streamActionFilter === item.key ? ' active' : ''}`}
                  onClick={() => {
                    setRuleRunPage(1);
                    setStreamActionFilter(
                      streamActionFilter === item.key ? 'all' : (item.key as AutopilotActionType)
                    );
                  }}
                >
                  {item.label}
                </button>
              ))}
              <span className="autopilot-filter-spacer" />
              <button type="button" className="btn btn-outline btn-sm" onClick={() => void loadRuleRunsData()}>
                刷新
              </button>
            </div>

            <div className="autopilot-table-wrap" style={{ position: 'relative' }}>
              <table className="autopilot-table">
                <colgroup>
                  <col style={{ width: 88 }} />
                  <col style={{ width: 220 }} />
                  <col style={{ width: 108 }} />
                  <col style={{ width: 300 }} />
                  <col style={{ width: 112 }} />
                  <col style={{ width: 260 }} />
                  <col style={{ width: 112 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>触发时间</th>
                    <th>规则名称</th>
                    <th>平台</th>
                    <th>对象</th>
                    <th>执行动作</th>
                    <th>指标</th>
                    <th>结果</th>
                  </tr>
                </thead>
                <tbody>
                  {streamLoading && ruleRuns.length === 0 && (
                    <tr><td colSpan={7}><TableLoadingCell /></td></tr>
                  )}
                  {ruleRuns.map((run) => {
                    const decision = decisionLabel(run.decision);
                    const actionType = runActionType(run);
                    return (
                      <Fragment key={run.id}>
                        <tr>
                          <td>{formatRelativeDayTime(run.created_at, AUTOPILOT_DISPLAY_TIMEZONE)}</td>
                          <td>
                            <div className="autopilot-ellipsis" title={run.rule_name}>
                              {run.rule_name}
                            </div>
                          </td>
                          <td>
                            <span className={`autopilot-platform-tag ${platformTagClassFromPlatform(run.platform)}`}>
                              {channelLabelFromPlatform(run.platform)}
                            </span>
                          </td>
                          <td>
                            <div className="autopilot-ellipsis" title={targetDisplay(run)}>
                              {targetDisplay(run)}
                            </div>
                          </td>
                          <td>
                            <span className={`autopilot-action-tag ${actionTagClass(actionType || undefined)}`}>
                              {actionTypeLabel(actionType || undefined)}
                            </span>
                          </td>
                          <td>
                            <div className="autopilot-ellipsis" title={runReason(run)}>
                              {runReason(run)}
                            </div>
                          </td>
                          <td>
                            <button
                              type="button"
                              className={`autopilot-result-tag ${decision.cls}`}
                              title={decision.text}
                              onClick={() => setExpandedRunID(expandedRunID === run.id ? null : run.id)}
                            >
                              {expandedRunID === run.id ? '收起详情' : decision.text}
                            </button>
                          </td>
                        </tr>
                        {expandedRunID === run.id && (
                          <tr>
                            <td colSpan={7} className="autopilot-expand-row">
                              <div className="autopilot-expand-grid">
                                {run.knowledge_description && (
                                  <div className="autopilot-expand-card">
                                    经验动作：{run.knowledge_description}
                                  </div>
                                )}
                                {(Array.isArray(run.trigger_snapshot) ? run.trigger_snapshot : []).map(
                                  (item, index) => (
                                    <div key={`${run.id}-${index}`} className="autopilot-expand-card">
                                      {triggerLine(item)}
                                    </div>
                                  )
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {!streamLoading && ruleRuns.length === 0 && (
                    <tr>
                      <td colSpan={7} className="autopilot-empty-cell">
                        暂无规则触发记录
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {streamLoading && ruleRuns.length > 0 && <TableLoadingOverlay />}
            </div>

            {ruleRunTotal > 0 && (
              <Pagination
                page={ruleRunPage}
                totalPages={ruleRunPageCount}
                onChange={(p) => setRuleRunPage(p)}
                total={`共 ${streamDisplayTotal} 条`}
                pageSize={ruleRunSize}
                pageSizeOptions={[10, 20, 50, 100]}
                onPageSizeChange={(nextPageSize) => { setRuleRunSize(nextPageSize); setRuleRunPage(1); }}
                disabled={streamLoading}
              />
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="autopilot-tab-panel active">
            <div className="autopilot-filter-bar">
              {[
                { key: 'all', label: '全部' },
                { key: 'success', label: '已执行' },
                { key: 'rejected', label: '已驳回' },
                { key: 'partial', label: '需人工观察' },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`autopilot-filter-chip${logFilter === item.key ? ' active' : ''}`}
                  onClick={() => {
                    setLogPage(1);
                    setLogFilter(item.key as 'all' | OperationLog['execution_result']);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="autopilot-table-wrap">
              <table className="autopilot-table">
                <colgroup>
                  <col style={{ width: 88 }} />
                  <col style={{ width: 220 }} />
                  <col style={{ width: 280 }} />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 250 }} />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 102 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>时间</th>
                    <th>规则</th>
                    <th>对象</th>
                    <th>账户所属人</th>
                    <th>经验动作</th>
                    <th>执行动作</th>
                    <th>结果</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedLogs.map((log) => {
                    const payload = operationPayload(log);
                    const status = executionResultLabel(log.execution_result);
                    return (
                      <tr key={log.id}>
                        <td>{formatRelativeDayTime(log.created_at, AUTOPILOT_DISPLAY_TIMEZONE)}</td>
                        <td>
                          <div className="autopilot-ellipsis" title={payload?.rule_name || '-'}>
                            {payload?.rule_name || '-'}
                          </div>
                        </td>
                        <td>
                          <div className="autopilot-ellipsis" title={queueTargetDisplay(payload)}>
                            {queueTargetDisplay(payload)}
                          </div>
                        </td>
                        <td>
                          <div className="autopilot-ellipsis" title={payload?.account_owner_name || '-'}>
                            {payload?.account_owner_name || '-'}
                          </div>
                        </td>
                        <td>
                          <div className="autopilot-ellipsis" title={payload?.knowledge_description || '-'}>
                            {payload?.knowledge_description || '-'}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`autopilot-action-tag ${actionTagClass(log.action_type)}`}
                            title={payload ? formatActionSummary(payload) : actionTypeLabel(log.action_type)}
                          >
                            {payload ? formatActionSummary(payload) : actionTypeLabel(log.action_type)}
                          </span>
                        </td>
                        <td>
                          <span className={`autopilot-result-tag ${status.cls}`} title={status.text}>{status.text}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {pagedLogs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="autopilot-empty-cell">
                        暂无操作历史
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {logListTotal > 0 && (
              <Pagination
                page={logPage}
                totalPages={logPageCount}
                onChange={(p) => setLogPage(p)}
                total={`共 ${logListTotal} 条`}
                pageSize={logSize}
                pageSizeOptions={[10, 20, 50, 100]}
                onPageSizeChange={(nextPageSize) => { setLogSize(nextPageSize); setLogPage(1); }}
                disabled={loading}
              />
            )}
          </div>
        )}

      </div>

      {confirmItem && (
        <div className="modal-overlay open" onClick={() => setConfirmItem(null)}>
          <div
            className="modal"
            style={{ width: 640, maxWidth: 'calc(100vw - 48px)' }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{confirmItem.action_type === 'notice' ? '通知确认' : '审批确认'}</h3>
              <button className="modal-close" onClick={() => setConfirmItem(null)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 16 }}>{confirmItem.suggestion.title}</div>
              <div
                style={{
                  padding: 18,
                  background: 'var(--surface2)',
                  borderRadius: 'var(--radius)',
                  marginBottom: 16,
                }}
              >
                <div style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 12, fontWeight: 600 }}>
                  规则命中说明
                </div>
                <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
                  {confirmItem.suggestion.reason}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{confirmItem.action_type === 'notice' ? '确认备注（可选）' : '审批备注（可选）'}</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={approvalNote}
                  onChange={(event) => setApprovalNote(event.target.value)}
                  placeholder="可输入审批备注..."
                  style={{ minHeight: 88 }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setConfirmItem(null)}>
                取消
              </button>
              <button className="btn btn-primary" onClick={() => void approveItem(confirmItem)}>
                {confirmItem.action_type === 'notice' ? '确认已知悉' : '确认批准'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectItem && (
        <div className="modal-overlay open" onClick={() => setRejectItem(null)}>
          <div
            className="modal"
            style={{ width: 640, maxWidth: 'calc(100vw - 48px)' }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h3>驳回建议</h3>
              <button className="modal-close" onClick={() => setRejectItem(null)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 14 }}>{rejectItem.suggestion.title}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {REJECT_PRESETS.map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    className="btn btn-outline btn-sm"
                    style={
                      rejectReason === reason
                        ? {
                            borderColor: 'var(--primary)',
                            color: 'var(--primary-light)',
                            background: 'rgba(18,16,184,.08)',
                          }
                        : undefined
                    }
                    onClick={() => setRejectReason(reason)}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              <div className="form-group">
                <label className="form-label">驳回原因</label>
                <textarea
                  className="form-textarea"
                  value={rejectReason}
                  onChange={(event) => setRejectReason(event.target.value)}
                  placeholder="请输入驳回原因..."
                  style={{ minHeight: 96 }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setRejectItem(null)}>
                取消
              </button>
              <button className="btn btn-primary" onClick={() => void rejectCurrentItem(rejectItem)}>
                确认驳回
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
